import { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useEffect, useRef } from 'react'
import { client } from '~/api/client'
import {
  getCoordinateById,
  getCoordinateItems,
  getCoordinateFolders,
  postCoordinatesCoordinateIdReceivesV1,
} from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { CoordinateDetail } from '~/features/CoordinateDetail/CoordinateDetail'
import { getDynamicLinkByCoordinateId } from '~/features/CoordinateDetail/lib/getDynamicLinkByCoordinateId'
import {
  getBreadcrumb,
  getMetaData,
} from '~/features/CoordinateDetail/lib/pageData'
import { CoordinateDetailProps } from '~/features/CoordinateDetail/types'
import { sendGTMEvent } from '~/features/shared/gtmEvent/sendGTMEvent'
import { assertSuccess } from '~/lib/assertSuccess'
import { COORDINATE_DETAIL_HEADER_ADJUST_URL } from '~/lib/constants'
import { isNetworkError } from '~/lib/isNetworkError'
import { openPopup } from '~/lib/openPopup'

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  { ssr: false }
)

const useAnalyzePageView = (coordinateId: number) => {
  useEffect(() => {
    postCoordinatesCoordinateIdReceivesV1(client, {
      parameter: { coordinate_id: coordinateId.toString() },
      requestBody: { uuid: crypto.randomUUID() },
    }).then(
      (res) => {
        assertSuccess(res)
      },
      (e) => {
        if (isNetworkError(e)) return undefined
        if (e.name === 'AbortError') {
          return undefined
        }
        throw new Error(`Failed to post pv count`, { cause: e })
      }
    )
  }, [coordinateId])
}

export const CoordinateDetailPage: NextPage<CoordinateDetailProps> = ({
  coordinate,
  coordinateItems,
  coordinateFolders,
}) => {
  useAnalyzePageView(coordinate.id)
  const dynamicLinkRef = useRef<Promise<string | undefined>>(undefined)

  return (
    <>
      <script>{`var subImagesInfo = {snapId: ${
        coordinate.id
      }, snapImagesCount: ${coordinate.sub_images.length + 1}}`}</script>
      <Layout
        breadcrumbList={getBreadcrumb(coordinate)}
        headerProps={{ hideAppIcon: true }}
        onClickAppLink={async (e: React.MouseEvent) => {
          e.preventDefault()
          sendGTMEvent({
            event: 'hdr_dynamiclink',
            eventCategory: 'link',
            pageGroup: 'content_h',
          })
          if (dynamicLinkRef.current === undefined)
            dynamicLinkRef.current = getDynamicLinkByCoordinateId(
              coordinate.id,
              COORDINATE_DETAIL_HEADER_ADJUST_URL
            )
          openPopup(dynamicLinkRef.current, COORDINATE_DETAIL_HEADER_ADJUST_URL)
        }}
        disableAppUrl
        abstractUrl="wear.jp/username/coordinateid/"
      >
        <Meta {...getMetaData(coordinate, coordinateItems)} />
        <GoogleAdScripts
          pages={[
            { label: 'pc:coordinate:detail' },
            { label: 'sp:coordinate:detail' },
            { label: 'sp' },
          ]}
        />
        <CoordinateDetail
          coordinate={coordinate}
          coordinateItems={coordinateItems}
          coordinateFolders={coordinateFolders}
        />
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<
  CoordinateDetailProps
  // eslint-disable-next-line complexity
> = async (context) => {
  const coordinateId = context.params?.coordinateId as string
  const [coordinateRes, coordinateItemsRes, coordinateFoldersRes] =
    await Promise.allSettled([
      getCoordinateById(client, { parameter: { coordinate_id: coordinateId } }),
      getCoordinateItems(
        client,
        { parameter: { coordinate_id: coordinateId } },
        { timeout: 1500 }
      ),
      getCoordinateFolders(
        client,
        { parameter: { coordinate_id: coordinateId } },
        { timeout: 1500 }
      ),
    ])

  if (coordinateRes.status === 'rejected') {
    throw new Error(`Failed to fetch coordinate: ${coordinateRes.reason}`)
  }
  if (coordinateRes.value.type === 'error') {
    if (coordinateRes.value.raw.status === 404) return { notFound: true }
    throw new Error(
      `Failed to fetch coordiante: ${coordinateRes.value.raw.statusText}`
    )
  }
  const coordinate = coordinateRes.value.data.coordinate_wrapper.coordinate

  const coordinateItems =
    coordinateItemsRes.status === 'fulfilled' &&
    coordinateItemsRes.value.type === 'success'
      ? coordinateItemsRes.value.data.coordinate_items
      : []

  const coordinateFolders =
    coordinateFoldersRes.status === 'fulfilled' &&
    coordinateFoldersRes.value.type === 'success'
      ? coordinateFoldersRes.value.data.folders
      : []

  // usernameが異なるURLでアクセスされた場合に正しいURLへリダイレクト
  // 例外的なアクセスのためクエリパラメータの引き継ぎは考慮しない
  const userName = coordinate.member.user_name
  if (
    typeof context.params?.username !== 'string' ||
    context.params.username.toLowerCase() !== userName.toLowerCase()
  ) {
    return {
      redirect: {
        permanent: true,
        destination: `/${userName}/${coordinateId}/`,
      },
    }
  }

  return { props: { coordinate, coordinateItems, coordinateFolders } }
}

export default CoordinateDetailPage
